// src/components/ui/Icon.jsx
import React from "react";

/**
 * Inline-SVG icon component for the icons that absolutely have to render
 * even if the Tabler webfont isn't loaded. Falls back to <i class="ti ti-...">
 * for any name not in this map — those still rely on the webfont being
 * available (install `@tabler/icons-webfont` to make that work).
 *
 * Usage:
 *   <Icon name="sun" size={16} />
 *   <Icon name="x"   size={14} className="text-red-500" />
 */
const PATHS = {
  // Theme toggle
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7" />
    </>
  ),
  moon: <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />,

  // Modal / nav
  x: <path d="M18 6l-12 12M6 6l12 12" />,
  "menu-2": <path d="M4 6l16 0M4 12l16 0M4 18l16 0" />,

  // Status
  check: <path d="M5 12l5 5l10 -10" />,
  "circle-check": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2l4 -4" />
    </>
  ),
  "alert-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16v.01" />
    </>
  ),
  "loader-2": <path d="M12 3a9 9 0 1 0 9 9" />,

  // Arrows
  "arrow-right": (
    <>
      <path d="M5 12l14 0" />
      <path d="M13 18l6 -6" />
      <path d="M13 6l6 6" />
    </>
  ),
  "arrow-left": (
    <>
      <path d="M5 12l14 0" />
      <path d="M5 12l6 6" />
      <path d="M5 12l6 -6" />
    </>
  ),

  // Stepper
  plus: <path d="M12 5l0 14M5 12l14 0" />,
  minus: <path d="M5 12l14 0" />,

  // LottoGPT brand mark (used by Logo)
  "lottogpt-mark": (
    <>
      <path d="M5 8 L9 4 L15 4 L19 8 L19 16 L15 20 L9 20 L5 16 Z" />
      <circle cx="9"  cy="9"  r="1.6" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9"  r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="14" r="1.6" fill="currentColor" stroke="none" />
      <path d="M9 9 L12 14 M15 9 L12 14 M9 9 L15 9" />
    </>
  ),

  // Commonly-used utility icons
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0 -2 -2H6a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2M4 5v4h4" />
      <path d="M4 13a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" />
    </>
  ),
  brain: (
    <>
      <path d="M15.5 13a3.5 3.5 0 0 0 -3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8" />
      <path d="M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1 -7 0v-1.8" />
      <path d="M17.5 16a3.5 3.5 0 0 0 0 -7h-.5" />
      <path d="M19 9.3v-2.8a3.5 3.5 0 0 0 -7 0" />
      <path d="M6.5 16a3.5 3.5 0 0 1 0 -7h.5" />
      <path d="M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8 -1.3 8 -3v-6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8 -1.3 8 -3v-6" />
    </>
  ),
  ticket: (
    <>
      <path d="M15 5v2M15 11v2M15 17v2" />
      <path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-3a2 2 0 0 0 0 -4v-3a2 2 0 0 1 2 -2" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.8 9a2 2 0 0 0 -1.8 -1h-2a2 2 0 1 0 0 4h2a2 2 0 1 1 0 4h-2a2 2 0 0 1 -1.8 -1" />
      <path d="M12 6v2M12 16v2" />
    </>
  ),
  sparkles: <path d="M3 12l3 -2l3 2l-3 2zM15 4l3 -2l3 2l-3 2zM15 20l3 -2l3 2l-3 2zM3 4l3 -2l3 2l-3 2zM9 12l6 -8M9 12l6 8" />,
  "shield-check": (
    <>
      <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
      <path d="M9 12l2 2l4 -4" />
    </>
  ),
  eye: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" />
    </>
  ),
  "eye-off": (
    <>
      <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
      <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-4 0 -7.333 -2.333 -10 -7c1.369 -2.394 2.913 -4.175 4.631 -5.344m3.367 -1.483A9.91 9.91 0 0 1 12 4c4 0 7.333 2.333 10 7c-.671 1.174 -1.402 2.193 -2.193 3.075" />
      <path d="M3 3l18 18" />
    </>
  ),
  send: (
    <path d="M10 14l11 -11M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -8l-8 -3.5a.55 .55 0 0 1 0 -1z" />
  ),
  "credit-card": (
    <>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M3 10l18 0M7 15l.01 0M11 15l2 0" />
    </>
  ),
  star: <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" />,
  "star-filled": (
    <path
      d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z"
      fill="currentColor"
    />
  ),
  bolt: <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />,
  "help-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v.01" />
      <path d="M12 13a2 2 0 0 0 .914 -3.782a1.98 1.98 0 0 0 -2.414 .483" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 21l8 0M12 17l0 4M7 4l10 0" />
      <path d="M17 4v8a5 5 0 0 1 -10 0v-8" />
      <path d="M5 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0M19 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    </>
  ),
  "ticket-off": (
    <path d="M15 5v2m0 4v1M3 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4M3 9v3a2 2 0 0 1 0 4v3h12M3 3l18 18" />
  ),
  history: (
    <>
      <path d="M12 8l0 4l3 3M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" />
    </>
  ),
};

export default function Icon({
  name,
  size = 16,
  strokeWidth = 2,
  className = "",
  style = {},
  ...rest
}) {
  const path = PATHS[name];

  // Fallback to Tabler webfont icon if we don't have an inline version
  if (!path) {
    return (
      <i
        className={`ti ti-${name} ${className}`}
        style={{ fontSize: size, ...style }}
        aria-hidden="true"
        {...rest}
      />
    );
  }

  // loader-2 spins via CSS class defined in tokens.css
  const spinClass = name === "loader-2" ? "spin" : "";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${spinClass} ${className}`.trim()}
      style={{ display: "inline-block", verticalAlign: "middle", ...style }}
      aria-hidden="true"
      {...rest}
    >
      {path}
    </svg>
  );
}
